import React, {memo, useCallback, useEffect, useMemo, useRef, useState} from "react"
import {SettingsPageContainer} from "../../../../components/Settings/SettingsPageContainer"
import {PasswordContainer} from "../../../../components/PasswordContainer"
import {useLocalization} from "../../../../hooks/localization"
import {useRPC} from "../../../../hooks/rpc"
import {SettingCheckBoxComponent} from "../../../../components/Settings/SettingCheckboxComponent"
import {useHistory} from "react-router-dom"
import {useMessage} from "../../../../hooks/message"
import {useDispatch, useSelector} from "react-redux"
import {useKeyboard} from "../../../../hooks/keyboard"
import {changeBusyAction} from "../../../../store/ui/actions"
import "./index.scss"

function formatTime(value: string): string {
    const cleanedValue = value.replace(/[^\d]/g, "")
    if (cleanedValue === "" || cleanedValue.length > 4) {
        return cleanedValue
    }
    let hours = cleanedValue.substring(0, 2)
    let minutes = cleanedValue.substring(2, 4)
    if (parseInt(hours, 10) > 23) {
        hours = "23"
    }
    if (parseInt(minutes, 10) > 59) {
        minutes = "59"
    }
    return `${hours}:${minutes}`
}
let timer
const BACK_URL = "/settings/advanced-settings"
export const AutoDesktop = memo(function AutoDesktop() {
    const {t} = useLocalization()
    const [rpc] = useRPC()
    const history = useHistory()
    const timeRef = useRef(null)
    const {success, failure} = useMessage()
    const dispatch = useDispatch()
    const supervisorConfigAutoRestart = useSelector(state => state.control.supConfigList?.auto_restart)
    const busySelector = useSelector(state => state.ui.busy)
    const [activePassive, setActivePassive] = useState<boolean>(supervisorConfigAutoRestart?.is_enabled || false)
    const [desktop, setDesktop] = useState<boolean>(supervisorConfigAutoRestart?.desktop || false)
    const [supWorker, setSupWorker] = useState<boolean>(supervisorConfigAutoRestart?.services || false)
    const [rtspV2, setRtspV2] = useState<boolean>(supervisorConfigAutoRestart?.rtspV2_service || false)
    const [time, setTime] = useState<string>(supervisorConfigAutoRestart?.time || "")
    const [setActiveNumpadExtendedInput, renderNumpadExtendedKeyboard] = useKeyboard("numpad-extended", true)

    const onClickSave = useCallback(() => {
        const desktopState = {
            is_enabled: activePassive,
            desktop,
            services: supWorker,
            rtspV2_service: rtspV2,
            time
        }
        return rpc.updateSystemRestartSettings(desktopState).catch(failure)
    }, [failure, rpc, activePassive, supWorker, rtspV2, time, desktop])

    const onFocusTime = useCallback(
        () => setActiveNumpadExtendedInput([timeRef, time, setTime]),
        [setActiveNumpadExtendedInput, time]
    )

    const onChangeTime = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => setTime(formatTime(e.target.value)),
        []
    )

    const onBlurNumber = useCallback(() => setActiveNumpadExtendedInput(null), [setActiveNumpadExtendedInput])

    const buttons = useMemo(
        () => [
            {
                text: t("save"),
                className: "green",
                onClick: onClickSave
            }
        ],
        [t, onClickSave]
    )

    useEffect(() => {
        if (busySelector) {
            timer = setTimeout(() => {
                dispatch(changeBusyAction(false))
                success(t("successfully_saved"))
                history.push(BACK_URL)
            }, 12000)
        } else {
            clearTimeout(timer)
        }
    }, [t, dispatch, success, history, busySelector])

    return (
        <PasswordContainer requiredRole="service" isFullscreen={true}>
            <SettingsPageContainer
                buttons={buttons}
                className="service-edit"
                backUrl={"/settings/advanced-settings"}
                style={null}
            >
                <div className="content-divider">
                    <div className="content-wrapper">
                        <SettingCheckBoxComponent
                            label={t("active_passive")}
                            value={activePassive}
                            set={() => setActivePassive(p => !p)}
                        />
                        <SettingCheckBoxComponent
                            label={t("desktop")}
                            value={desktop}
                            set={() => setDesktop(p => !p)}
                        />
                        <SettingCheckBoxComponent
                            label="Sup/Worker"
                            value={supWorker}
                            set={() => setSupWorker(p => !p)}
                        />
                        <SettingCheckBoxComponent label="RtspV2" value={rtspV2} set={() => setRtspV2(p => !p)} />
                        <div className="customized-time-input">
                            <p>{t("time")}</p>
                            <input
                                maxLength={5}
                                type="text"
                                ref={timeRef}
                                onBlur={onBlurNumber}
                                onChange={onChangeTime}
                                onFocus={onFocusTime}
                                value={time}
                                placeholder="--:--"
                            />
                        </div>
                    </div>
                    <div className="content-wrapper"></div>
                </div>
            </SettingsPageContainer>
            {renderNumpadExtendedKeyboard()}
        </PasswordContainer>
    )
})
